import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from "@carbon/react";
import { PurchaseRequestItemV2, PurchaseRequestV2 } from "../../../types";
import { formatDate } from "../../../utils";
import { currencyFormatter } from "../../../utils/utils";
import { getRequesterName } from "../purchaseRequestListHelpers";

export interface DetailSummaryListProps {
  request: PurchaseRequestV2;
  firstItem?: PurchaseRequestItemV2;
  /** The read-only budget row is shown only when every item shares one budget AND the viewer can't
   * edit it (the editor replaces it otherwise). */
  readOnlyBudget?: { value?: number; currency?: string } | null;
}

/**
 * The request summary panel's label/value rows (Task 5) — company, requester, created date, needed
 * dates, document class and (read-only) budget, on Carbon `StructuredList`. Presentational; every
 * field and its order matches the pre-Carbon info panel.
 */
const DetailSummaryList = ({
  request,
  firstItem,
  readOnlyBudget,
}: DetailSummaryListProps) => {
  const { t } = useTranslation();

  const row = (label: string, value: ReactNode) => (
    <StructuredListRow>
      <StructuredListCell className="pr-detail__info-label">
        {label}
      </StructuredListCell>
      <StructuredListCell>{value}</StructuredListCell>
    </StructuredListRow>
  );

  return (
    <StructuredListWrapper>
      <StructuredListBody>
        {row(t("purchaseRequests.fields.company"), request.company?.name)}
        {row(
          t("purchaseRequests.fields.requester"),
          getRequesterName(request)
        )}
        {row(t("purchaseRequests.fields.createdAt"), formatDate(request.createdAt))}
        {firstItem?.neededFrom
          ? row(
              t("purchaseRequests.items.columns.needed"),
              <>
                {formatDate(firstItem.neededFrom)}
                {firstItem.neededTo &&
                  firstItem.neededTo !== firstItem.neededFrom &&
                  ` - ${formatDate(firstItem.neededTo)}`}
              </>
            )
          : null}
        {request.documentClass?.code
          ? row(
              t("purchaseRequests.documentClass"),
              <>
                {request.documentClass.code}
                {request.documentClass.description &&
                  ` — ${request.documentClass.description}`}
              </>
            )
          : null}
        {readOnlyBudget
          ? row(
              t("purchaseRequests.items.columns.budgetValue"),
              currencyFormatter(
                readOnlyBudget.value ?? undefined,
                readOnlyBudget.currency || undefined
              ) || "-"
            )
          : null}
      </StructuredListBody>
    </StructuredListWrapper>
  );
};

export default DetailSummaryList;
